import { useEffect, useState } from "react";
import {
  WHATSAPP_WS_PATH,
  type WhatsappLogEntry,
  type WhatsappServerEvent,
  type WhatsappSessions,
} from "@shared/api";
import { getToken } from "@client/api/token";

export interface WhatsappSocketState {
  /** El WebSocket del panel está abierto (no implica WhatsApp conectado). */
  connected: boolean;
  /** Lista de sesiones en vivo (metadatos + estado). */
  sessions: WhatsappSessions;
  /** Logs en vivo por sesión. */
  logs: Record<string, WhatsappLogEntry[]>;
}

// Escucha el canal en tiempo real: lista de sesiones, QR, estado, logs.
export function useWhatsappSocket(): WhatsappSocketState {
  const [connected, setConnected] = useState(false);
  const [sessions, setSessions] = useState<WhatsappSessions>([]);
  const [logs, setLogs] = useState<Record<string, WhatsappLogEntry[]>>({});

  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let closed = false;

    const connect = (): void => {
      const token = getToken();
      if (!token) return;
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${protocol}://${window.location.host}${WHATSAPP_WS_PATH}?token=${encodeURIComponent(token)}`;
      socket = new WebSocket(url);

      socket.addEventListener("open", () => {
        attempts = 0;
        setConnected(true);
      });

      socket.addEventListener("message", (event) => {
        try {
          const parsed = JSON.parse(String(event.data)) as WhatsappServerEvent;
          if (parsed.type === "sessions") {
            setSessions(parsed.data);
          } else if (parsed.type === "status") {
            setSessions((prev) =>
              prev.map((session) =>
                session.id === parsed.sessionId ? { ...session, status: parsed.data } : session,
              ),
            );
          } else if (parsed.type === "log") {
            setLogs((prev) => ({
              ...prev,
              [parsed.sessionId]: [parsed.data, ...(prev[parsed.sessionId] ?? [])].slice(0, 50),
            }));
          }
        } catch {
          // Mensaje no parseable: lo ignoramos.
        }
      });

      socket.addEventListener("close", () => {
        setConnected(false);
        if (closed) return;
        attempts += 1;
        const delay = Math.min(15_000, 1_000 * 2 ** Math.min(attempts, 4));
        reconnectTimer = setTimeout(connect, delay);
      });

      socket.addEventListener("error", () => {
        socket?.close();
      });
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      // Cerrar un socket a mitad de handshake provoca ECONNABORTED en el proxy
      // de Vite (p. ej. con StrictMode); esperamos a "open" antes de cerrar.
      const current = socket;
      if (!current) return;
      if (current.readyState === WebSocket.CONNECTING) {
        current.addEventListener("open", () => current.close(), { once: true });
      } else if (current.readyState === WebSocket.OPEN) {
        current.close();
      }
    };
  }, []);

  return { connected, sessions, logs };
}
